export const AGENDA_ENTRY_TYPES = Object.freeze({
  APPOINTMENT: 'appointment',
  SCHEDULED_MESSAGE: 'scheduled_message',
});

export const AGENDA_HIDDEN_TYPES_KEY = 'conversation_agenda_hidden_types';

const UNIX_MS_THRESHOLD = 1e12;
const MAX_RECURRENT_STEPS = 400;

export function hiddenAgendaTypes(uiSettings) {
  const hidden = uiSettings?.[AGENDA_HIDDEN_TYPES_KEY];
  if (!Array.isArray(hidden)) return [];
  return hidden.filter(Boolean);
}

export function isAgendaTypeVisible(hiddenTypes, type) {
  return !(hiddenTypes || []).includes(type);
}

export function toggleHiddenAgendaType(hiddenTypes, type) {
  const next = new Set(hiddenTypes || []);
  if (next.has(type)) next.delete(type);
  else next.add(type);
  return Array.from(next);
}

export function parseAgendaTimestamp(value) {
  if (value == null || value === '') return NaN;
  if (typeof value === 'number') {
    if (!Number.isFinite(value)) return NaN;
    return value < UNIX_MS_THRESHOLD ? value * 1000 : value;
  }
  const raw = String(value).trim();
  if (!raw) return NaN;
  if (/^\d+(\.\d+)?$/.test(raw)) {
    const numeric = Number(raw);
    if (!Number.isFinite(numeric)) return NaN;
    return numeric < UNIX_MS_THRESHOLD ? numeric * 1000 : numeric;
  }
  return new Date(raw).getTime();
}

export function periodType(period) {
  if (period && typeof period === 'object') {
    return period.type || 'every_day';
  }
  return period || 'every_day';
}

export function advanceRecurrentOccurrence(ms, type) {
  const date = new Date(ms);
  if (!Number.isFinite(date.getTime())) return NaN;
  switch (type) {
    case 'every_week':
      date.setDate(date.getDate() + 7);
      break;
    case 'every_month':
      date.setMonth(date.getMonth() + 1);
      break;
    case 'every_weekday':
      do {
        date.setDate(date.getDate() + 1);
      } while (date.getDay() === 0 || date.getDay() === 6);
      break;
    case 'every_weekend':
      do {
        date.setDate(date.getDate() + 1);
      } while (date.getDay() !== 0 && date.getDay() !== 6);
      break;
    case 'every_day':
    case 'every_day_at_time':
    default:
      date.setDate(date.getDate() + 1);
  }
  return date.getTime();
}

export function nextOccurrenceIso(
  scheduledAt,
  { isRecurrent = false, period = null } = {},
  now = Date.now()
) {
  const start = parseAgendaTimestamp(scheduledAt);
  if (!Number.isFinite(start)) return '';
  if (!isRecurrent || start >= now) return new Date(start).toISOString();

  let at = start;
  const type = periodType(period);
  for (let step = 0; step < MAX_RECURRENT_STEPS && at < now; step += 1) {
    const next = advanceRecurrentOccurrence(at, type);
    if (!Number.isFinite(next) || next <= at) break;
    at = next;
  }
  return new Date(at).toISOString();
}

export function resolveAuthorName({ userId, userName, agents = [] } = {}) {
  const named = typeof userName === 'string' ? userName.trim() : '';
  if (named) return named;
  if (userId == null || userId === '') return '';
  const agent = (agents || []).find(
    entry => Number(entry?.id) === Number(userId)
  );
  const agentName = typeof agent?.name === 'string' ? agent.name.trim() : '';
  return agentName;
}

// Compromisso nao tem autor exibivel hoje. Verificado nos 500 cards da conta 11
// em 20/08/2026: item_details nao guarda nenhum campo de autor (as chaves em uso
// sao title, description, offers, notes, value, scheduled_at, agent_id e afins).
// agent_id existe, mas e o responsavel pelo card, nao quem agendou, entao usar
// ele aqui seria dado enganoso.
//
// A autoria real vive no LeadAppointment (created_by_id, desde a fatia de
// autoria). Quando a API do kanban expuser o appointment vinculado, e daqui que
// o nome deve sair. Ate la, vazio e a resposta honesta.
export function appointmentAuthor() {
  return '';
}

export function scheduledMessageAuthor(message, agents = []) {
  return resolveAuthorName({
    userId: message?.user_id ?? message?.user?.id,
    userName: message?.user_name || message?.user?.name || '',
    agents,
  });
}

export function isPendingScheduledMessage(message) {
  const status = message?.status;
  return !status || status === 'pending';
}

export function extractScheduledMessages(payload) {
  if (Array.isArray(payload)) return payload;
  return payload?.payload || payload?.scheduled_messages || [];
}

export function buildAppointmentEntries(items, { contactName = '' } = {}) {
  return (items || [])
    .map(item => {
      const iso = nextOccurrenceIso(
        item?.item_details?.scheduled_at || item?.scheduled_at
      );
      if (!iso) return null;
      return {
        key: `${AGENDA_ENTRY_TYPES.APPOINTMENT}-${item.id}`,
        id: item.id,
        type: AGENDA_ENTRY_TYPES.APPOINTMENT,
        iso,
        at: parseAgendaTimestamp(iso),
        title: item.item_details?.title || contactName || '',
        author: appointmentAuthor(),
        recurrent: false,
        item,
      };
    })
    .filter(Boolean);
}

export function buildScheduledMessageEntries(
  messages,
  { agents = [] } = {},
  now = Date.now()
) {
  return (messages || [])
    .filter(isPendingScheduledMessage)
    .map(message => {
      const iso = nextOccurrenceIso(
        message.scheduled_at,
        {
          isRecurrent: Boolean(message.is_recurrent),
          period: message.period,
        },
        now
      );
      if (!iso) return null;
      return {
        key: `${AGENDA_ENTRY_TYPES.SCHEDULED_MESSAGE}-${message.id}`,
        id: message.id,
        type: AGENDA_ENTRY_TYPES.SCHEDULED_MESSAGE,
        iso,
        at: parseAgendaTimestamp(iso),
        title: message.title || message.message || message.content || '',
        author: scheduledMessageAuthor(message, agents),
        recurrent: Boolean(message.is_recurrent),
        item: message,
      };
    })
    .filter(Boolean);
}

export function mergeAgendaEntries(
  { appointments = [], messages = [], contactName = '', agents = [] } = {},
  now = Date.now()
) {
  return [
    ...buildAppointmentEntries(appointments, { contactName }),
    ...buildScheduledMessageEntries(messages, { agents }, now),
  ].sort((left, right) => left.at - right.at);
}

export function filterAgendaEntries(entries, hiddenTypes) {
  return (entries || []).filter(entry =>
    isAgendaTypeVisible(hiddenTypes, entry.type)
  );
}

export function pickNextAgendaEntry(entries, now = Date.now()) {
  const upcoming = (entries || [])
    .filter(entry => Number.isFinite(entry.at))
    .sort((left, right) => left.at - right.at);
  if (!upcoming.length) return null;
  return (
    upcoming.find(entry => entry.at >= now) || upcoming[upcoming.length - 1]
  );
}
